import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, Chip, Switch, Stack, Skeleton, Paper, alpha, useTheme,
} from '@mui/material';
import { Inbox } from '@mui/icons-material';
import HubServiceTableCell from './HubServiceTableCell';
import ServiceQuickActions from './ServiceQuickActions';
import type { HubService, ServiceHubType } from '../../constants/serviceHubs';

export type HubServiceDisplayRow = HubService & {
  displayTitle: string;
  displaySubtitle: string;
};

interface Props {
  rows: HubServiceDisplayRow[];
  loading?: boolean;
  hub: ServiceHubType;
  accentColor: string;
  canMutate?: boolean;
  togglePendingId?: string | null;
  onOpen: (row: HubServiceDisplayRow) => void;
  onToggleActive: (row: HubServiceDisplayRow, isActive: boolean) => void;
  onEdit?: (row: HubServiceDisplayRow) => void;
  onDelete?: (id: string) => void;
}

const headSx = {
  fontWeight: 650,
  fontSize: '0.68rem',
  letterSpacing: '0.08em',
  textTransform: 'uppercase' as const,
  bgcolor: 'action.hover',
  color: 'text.secondary',
  py: 1.35,
};

const actionsColSx = {
  width: 120,
  minWidth: 120,
  whiteSpace: 'nowrap' as const,
};

export default function HubServicesTable({
  rows,
  loading,
  hub,
  accentColor,
  canMutate,
  togglePendingId,
  onOpen,
  onToggleActive,
  onEdit,
  onDelete,
}: Props) {
  const theme = useTheme();

  if (loading) {
    return (
      <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider' }}>
        {[1, 2, 3, 4].map((i) => <Skeleton key={i} height={64} sx={{ mx: 2, my: 0.75 }} />)}
      </Paper>
    );
  }

  if (!rows.length) {
    return (
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: 1,
          borderColor: 'divider',
          borderStyle: 'dashed',
          py: 7,
          textAlign: 'center',
          bgcolor: alpha(theme.palette.primary.main, 0.02),
        }}
      >
        <Inbox sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700} color="text.secondary">No sub-services</Typography>
        <Typography variant="body2" color="text.disabled">Add a service to get started</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ borderRadius: 2.5, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
      <TableContainer>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headSx, width: 56 }}>S.No</TableCell>
              <TableCell sx={headSx}>Service</TableCell>
              <TableCell sx={{ ...headSx, display: { xs: 'none', md: 'table-cell' } }}>Type</TableCell>
              <TableCell sx={headSx}>Status</TableCell>
              <TableCell sx={{ ...headSx, ...actionsColSx }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <TableRow
                key={row._id}
                hover
                onClick={() => onOpen(row)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
                  '& td': { py: 1.35 },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight={600} color="text.secondary">{index + 1}</Typography>
                </TableCell>
                <TableCell>
                  <HubServiceTableCell
                    service={{
                      name: row.displayTitle || row.name,
                      icon: row.icon,
                      themeColor: row.themeColor,
                    }}
                    subtitle={row.displaySubtitle || row.description}
                    accentColor={accentColor}
                  />
                </TableCell>
                <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                  <Chip
                    size="small"
                    label={row.isSystem ? 'Core' : 'Custom'}
                    variant="outlined"
                    color={row.isSystem ? 'primary' : 'default'}
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                  />
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip
                      size="small"
                      label={row.isActive ? 'Active' : 'Off'}
                      color={row.isActive ? 'success' : 'default'}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700 }}
                    />
                    <Switch
                      size="small"
                      checked={!!row.isActive}
                      disabled={togglePendingId === row._id}
                      onChange={(e) => onToggleActive(row, e.target.checked)}
                    />
                  </Stack>
                </TableCell>
                <TableCell sx={actionsColSx} align="right" onClick={(e) => e.stopPropagation()}>
                  <Stack direction="row" justifyContent="flex-end" alignItems="center">
                    <ServiceQuickActions
                      service={row}
                      hubType={hub}
                      onView={() => onOpen(row)}
                      onEdit={canMutate && onEdit ? () => onEdit(row) : undefined}
                      onDelete={canMutate ? onDelete : undefined}
                    />
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
